import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MessageCircle, Paperclip, CheckCircle2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { useLang } from "@/i18n/translations";
import { API, COMPANY } from "@/lib/siteConfig";

const initialForm = {
  name: "",
  company: "",
  email: "",
  phone: "",
  project_type: "",
  industry: "",
  budget: "",
  timeline: "",
  description: "",
  preferred_contact: "email",
};

export const ContactSection = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState(initialForm);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error(t.contact.fileHint);
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.project_type || form.description.trim().length < 5) {
      toast.error(t.contact.errorRequired);
      return;
    }
    setSubmitting(true);
    try {
      let fileId = null;
      if (file) {
        try {
          const fd = new FormData();
          fd.append("file", file);
          const up = await axios.post(`${API}/uploads`, fd);
          fileId = up.data.file_id;
        } catch (err) {
          toast.error(t.contact.errorFile);
        }
      }
      await axios.post(`${API}/leads`, { ...form, file_id: fileId, language: lang });
      setSuccess(true);
    } catch (err) {
      toast.error(t.contact.errorSubmit);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm(initialForm);
    setFile(null);
    setSuccess(false);
  };

  const contactIcons = { email: Mail, phone: Phone, whatsapp: MessageCircle };

  return (
    <section id="kontakt" data-testid="contact-section" className="relative py-20 sm:py-28 bg-[#070a12] border-t border-white/5 overflow-hidden">
      <div className="absolute inset-0 cs-glow-cyan opacity-60" aria-hidden="true" />
      <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10">
        <SectionHeading kicker={t.contact.kicker} title={t.contact.title} subtitle={t.contact.subtitle} testId="contact-heading" />

        <Reveal className="mt-12 max-w-3xl">
          {success ? (
            <div data-testid="contact-success" className="rounded-2xl border border-[#00d4ff]/25 bg-white/[0.03] p-10 text-center">
              <CheckCircle2 className="w-12 h-12 text-[#00d4ff] mx-auto" />
              <h3 className="mt-5 font-display text-2xl font-semibold text-foreground">{t.contact.successTitle}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{t.contact.successText}</p>
              <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
                {COMPANY.whatsapp ? (
                  <a
                    data-testid="contact-success-whatsapp-button"
                    href={`https://wa.me/${COMPANY.whatsapp.replace(/[^0-9]/g, "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-[#05070d] font-medium px-6 py-2.5 text-sm hover:opacity-90 transition-opacity duration-200"
                  >
                    <MessageCircle className="w-4 h-4" /> {t.contact.successWhatsapp}
                  </a>
                ) : (
                  <a
                    data-testid="contact-success-email-button"
                    href={`mailto:${COMPANY.email}`}
                    className="inline-flex items-center gap-2 rounded-full bg-[#00d4ff] text-[#05070d] font-medium px-6 py-2.5 text-sm hover:bg-[#33ddff] transition-colors duration-200"
                  >
                    <Mail className="w-4 h-4" /> {t.contact.successEmail}: {COMPANY.email}
                  </a>
                )}
                <button
                  data-testid="contact-new-request-button"
                  onClick={reset}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200 px-4 py-2"
                >
                  {t.contact.newRequest}
                </button>
              </div>
            </div>
          ) : (
            <form data-testid="contact-lead-form" onSubmit={submit} className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 sm:p-8 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-mono-cs uppercase tracking-wider text-muted-foreground mb-2" htmlFor="c-name">
                    {t.contact.name} *
                  </label>
                  <Input id="c-name" data-testid="contact-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-white/[0.04] border-white/10 h-11" />
                </div>
                <div>
                  <label className="block text-xs font-mono-cs uppercase tracking-wider text-muted-foreground mb-2" htmlFor="c-company">
                    {t.contact.company}
                  </label>
                  <Input id="c-company" data-testid="contact-company-input" value={form.company} onChange={(e) => set("company", e.target.value)} className="bg-white/[0.04] border-white/10 h-11" />
                </div>
                <div>
                  <label className="block text-xs font-mono-cs uppercase tracking-wider text-muted-foreground mb-2" htmlFor="c-email">
                    {t.contact.email} *
                  </label>
                  <Input id="c-email" data-testid="contact-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-white/[0.04] border-white/10 h-11" />
                </div>
                <div>
                  <label className="block text-xs font-mono-cs uppercase tracking-wider text-muted-foreground mb-2" htmlFor="c-phone">
                    {t.contact.phone}
                  </label>
                  <Input id="c-phone" data-testid="contact-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-white/[0.04] border-white/10 h-11" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-mono-cs uppercase tracking-wider text-muted-foreground mb-2">{t.contact.projectType} *</label>
                  <Select value={form.project_type} onValueChange={(v) => set("project_type", v)}>
                    <SelectTrigger aria-label={t.contact.projectType} data-testid="contact-project-type-select" className="bg-white/[0.04] border-white/10 h-11">
                      <SelectValue placeholder={t.contact.projectTypePlaceholder} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0b0f1a] border-white/10">
                      {t.contact.projectTypes.map((pt) => (
                        <SelectItem key={pt} value={pt} data-testid={`contact-project-type-option-${pt.replace(/[^a-zA-Z]/g, "").toLowerCase()}`}>
                          {pt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-xs font-mono-cs uppercase tracking-wider text-muted-foreground mb-2">{t.contact.industry}</label>
                  <Select value={form.industry} onValueChange={(v) => set("industry", v)}>
                    <SelectTrigger aria-label={t.contact.industry} data-testid="contact-industry-select" className="bg-white/[0.04] border-white/10 h-11">
                      <SelectValue placeholder={t.contact.industryPlaceholder} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0b0f1a] border-white/10">
                      {t.contact.industryOptions.map((opt) => (
                        <SelectItem key={opt} value={opt}>
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-xs font-mono-cs uppercase tracking-wider text-muted-foreground mb-2">{t.contact.budget}</label>
                  <Select value={form.budget} onValueChange={(v) => set("budget", v)}>
                    <SelectTrigger aria-label={t.contact.budget} data-testid="contact-budget-select" className="bg-white/[0.04] border-white/10 h-11">
                      <SelectValue placeholder={t.contact.budgetPlaceholder} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0b0f1a] border-white/10">
                      {t.contact.budgetOptions.map((opt) => (
                        <SelectItem key={opt} value={opt}>
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-xs font-mono-cs uppercase tracking-wider text-muted-foreground mb-2">{t.contact.timeline}</label>
                  <Select value={form.timeline} onValueChange={(v) => set("timeline", v)}>
                    <SelectTrigger aria-label={t.contact.timeline} data-testid="contact-timeline-select" className="bg-white/[0.04] border-white/10 h-11">
                      <SelectValue placeholder={t.contact.timelinePlaceholder} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0b0f1a] border-white/10">
                      {t.contact.timelineOptions.map((opt) => (
                        <SelectItem key={opt} value={opt}>
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-mono-cs uppercase tracking-wider text-muted-foreground mb-2" htmlFor="c-desc">
                  {t.contact.description} *
                </label>
                <Textarea
                  id="c-desc"
                  data-testid="contact-description-textarea"
                  rows={5}
                  value={form.description}
                  onChange={(e) => set("description", e.target.value)}
                  placeholder={t.contact.descriptionPlaceholder}
                  className="bg-white/[0.04] border-white/10 resize-none"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 items-end">
                <div>
                  <label className="block text-xs font-mono-cs uppercase tracking-wider text-muted-foreground mb-2">{t.contact.file}</label>
                  <input ref={fileInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.zip,.txt" onChange={handleFile} className="hidden" data-testid="contact-file-input" />
                  {file ? (
                    <div className="flex items-center gap-2 h-11 px-4 rounded-md border border-[#00d4ff]/30 bg-[#00d4ff]/5 text-sm text-foreground/90">
                      <Paperclip className="w-4 h-4 text-[#00d4ff] shrink-0" />
                      <span className="truncate flex-1">{file.name}</span>
                      <button type="button" data-testid="contact-file-remove" onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }} className="text-muted-foreground hover:text-foreground">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      data-testid="contact-file-button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full h-11 px-4 rounded-md border border-dashed border-white/15 text-sm text-muted-foreground hover:border-white/30 hover:text-foreground transition-[border-color,color] duration-200 flex items-center gap-2"
                    >
                      <Paperclip className="w-4 h-4" /> {t.contact.fileHint}
                    </button>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-mono-cs uppercase tracking-wider text-muted-foreground mb-2">{t.contact.preferredContact}</label>
                  <div className="flex rounded-md border border-white/10 bg-white/[0.04] p-1 gap-1">
                    {Object.entries(t.contact.contactOptions).map(([key, label]) => {
                      const Icon = contactIcons[key];
                      return (
                        <button
                          key={key}
                          type="button"
                          data-testid={`contact-preferred-${key}`}
                          onClick={() => set("preferred_contact", key)}
                          className={`flex-1 h-9 rounded text-xs font-medium flex items-center justify-center gap-1.5 transition-[background-color,color] duration-200 ${
                            form.preferred_contact === key ? "bg-[#00d4ff] text-[#05070d]" : "text-muted-foreground hover:text-foreground"
                          }`}
                        >
                          <Icon className="w-3.5 h-3.5" /> {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <Button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={submitting}
                  size="lg"
                  className="w-full sm:w-auto bg-[#00d4ff] text-[#05070d] hover:bg-[#33ddff] font-medium rounded-full px-10 h-12 shadow-[0_0_36px_rgba(0,212,255,0.18)] transition-[background-color,box-shadow] duration-200 disabled:opacity-60"
                >
                  {submitting ? t.contact.submitting : t.contact.submit}
                </Button>
              </div>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
